import QRCode from 'qrcode';
import * as XLSX from 'xlsx';

export interface QRProductData {
  id: string;
  name: string;
  item_code: string;
  brand?: string;
  company_id: string;
}

/**
 * Generate universal QR link for a product
 * This creates a link that works universally for the company
 * SECURITY: Always includes company ID to prevent cross-company access
 */
export const generateUniversalProductLink = (productId: string, companyId: string): string => {
  if (!companyId) {
    throw new Error('Company ID is required for security. QR codes must be company-specific.');
  }
  
  // Create a universal link that includes the company context
  // When scanned, this will redirect to the product detail page
  const baseUrl = window.location.origin;
  return `${baseUrl}/qr/product/${productId}?company=${companyId}`;
};

/**
 * Generate QR code data URL for a product
 * SECURITY: Always validates company ID is present
 */
export const generateProductQRCode = async (productId: string, companyId: string): Promise<string> => {
  if (!companyId) {
    throw new Error('Company ID is required for security. QR codes must be company-specific.');
  }
  
  const qrData = generateUniversalProductLink(productId, companyId);
  
  try {
    const qrDataUrl = await QRCode.toDataURL(qrData, {
      width: 200,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
    return qrDataUrl;
  } catch (error) {
    console.error('QR code generation error:', error);
    return '';
  }
};

/**
 * Generate formatted QR code with product details and branding
 * SECURITY: Always validates company ID is present
 */
export const generateBrandedQRCode = async (
  product: QRProductData,
  options: {
    includeBranding?: boolean;
    size?: number;
  } = {}
): Promise<string> => {
  const { includeBranding = true, size = 200 } = options;
  
  if (!product.company_id) {
    throw new Error('Company ID is required for security. QR codes must be company-specific.');
  }
  
  // For individual product QR codes, we can include more detailed information
  const qrData = generateUniversalProductLink(product.id, product.company_id);
  
  try {
    const qrDataUrl = await QRCode.toDataURL(qrData, {
      width: size,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
    return qrDataUrl;
  } catch (error) {
    console.error('Branded QR code generation error:', error);
    return '';
  }
};

/**
 * Export products to Excel with QR codes
 * SECURITY: Always validates company ID is present and matches product company
 */
export const exportProductsToExcel = async (
  products: QRProductData[],
  companyId: string,
  filename?: string
): Promise<void> => {
  if (!companyId) {
    throw new Error('Company ID is required for security. QR codes must be company-specific.');
  }
  
  // Validate all products belong to the same company
  const invalidProducts = products.filter(p => p.company_id !== companyId);
  if (invalidProducts.length > 0) {
    throw new Error('All products must belong to the same company for security.');
  }
  
  // Create Excel data with QR links
  const excelData = products.map(product => ({
    'Ürün Kodu': product.item_code,
    'Ürün Adı': product.name,
    'Marka': product.brand || '',
    'QR Link': generateUniversalProductLink(product.id, companyId),
    'Oluşturulma Tarihi': new Date().toLocaleDateString('tr-TR'),
    'Site': 'www.musterix.com'
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Set column widths
  const columnWidths = [
    { wch: 15 }, // Ürün Kodu
    { wch: 30 }, // Ürün Adı  
    { wch: 15 }, // Marka
    { wch: 50 }, // QR Link
    { wch: 15 }, // Oluşturulma Tarihi
    { wch: 20 }  // Site
  ];
  worksheet['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'QR Kodları');

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  const finalFilename = filename || `qr-kodlari-${timestamp}.xlsx`;

  // Save file
  XLSX.writeFile(workbook, finalFilename);
};

/**
 * Parse QR code data to extract product information
 * SECURITY: Prioritizes company-validated formats, warns about legacy formats
 */
export const parseQRCode = (qrData: string): { productId: string; companyId?: string; isSecure?: boolean } | null => {
  try {
    console.log('🔍 Parsing QR code data:', qrData);
    
    // Handle different QR code formats
    
    // Universal link format: /qr/product/{id}?company={companyId} - MOST SECURE
    // This pattern now handles both full URLs and path-only strings
    const universalMatch = qrData.match(/(?:https?:\/\/[^\/]+)?\/qr\/product\/([^?]+)\?company=([^&]+)/);
    if (universalMatch) {
      console.log('✅ Matched universal QR format - Product ID:', universalMatch[1], 'Company ID:', universalMatch[2]);
      return {
        productId: universalMatch[1],
        companyId: universalMatch[2],
        isSecure: true
      };
    }
    
    // Direct product link format: /products/{id} - NO COMPANY VALIDATION
    // This pattern also handles both full URLs and path-only strings
    const directMatch = qrData.match(/(?:https?:\/\/[^\/]+)?\/products\/([^/?]+)/);
    if (directMatch) {
      console.warn('⚠️ QR code uses legacy format without company validation - Product ID:', directMatch[1]);
      return {
        productId: directMatch[1],
        isSecure: false
      };
    }
    
    // Legacy format: product-{id} - NO COMPANY VALIDATION
    if (qrData.includes('product-')) {
      const productId = qrData.replace('product-', '');
      console.warn('⚠️ QR code uses legacy product- format without company validation - Product ID:', productId);
      return {
        productId,
        isSecure: false
      };
    }
    
    // Direct ID format - NO COMPANY VALIDATION
    if (qrData.match(/^[a-f0-9-]{36}$/)) {
      console.warn('⚠️ QR code uses direct UUID format without company validation - Product ID:', qrData);
      return {
        productId: qrData,
        isSecure: false
      };
    }
    
    // No pattern matched
    console.error('❌ QR code format not recognized. Supported formats:');
    console.error('   - Universal: https://domain.com/qr/product/{id}?company={companyId}');
    console.error('   - Legacy: https://domain.com/products/{id}');
    console.error('   - Legacy: product-{id}');
    console.error('   - Legacy: {uuid}');
    console.error('   Received:', qrData);
    
    return null;
  } catch (error) {
    console.error('Error parsing QR code:', error);
    return null;
  }
};

/**
 * Test QR parsing function to verify different formats work correctly
 * This function can be called from browser console for debugging
 */
export const testQRParsing = () => {
  console.log('🧪 Testing QR parsing with different formats...');
  
  const testCases = [
    'https://yourapp.com/qr/product/123e4567-e89b-12d3-a456-426614174000?company=987fcdeb-51a2-43d7-8f9e-123456789012',
    '/qr/product/123e4567-e89b-12d3-a456-426614174000?company=987fcdeb-51a2-43d7-8f9e-123456789012',
    'https://yourapp.com/products/123e4567-e89b-12d3-a456-426614174000',
    '/products/123e4567-e89b-12d3-a456-426614174000',
    'product-123e4567-e89b-12d3-a456-426614174000',
    '123e4567-e89b-12d3-a456-426614174000',
    'invalid-qr-code-format'
  ];
  
  testCases.forEach((testCase, index) => {
    console.log(`\n--- Test ${index + 1}: ${testCase} ---`);
    const result = parseQRCode(testCase);
    console.log('Result:', result);
  });
  
  console.log('\n✅ QR parsing test completed!');
};

/**
 * Create a printable QR label with product information and branding
 */
export const createPrintableQRLabel = (
  product: QRProductData,
  qrCodeDataUrl: string,
  options: {
    includeBranding?: boolean;
    labelSize?: 'small' | 'medium' | 'large';
  } = {}
): string => {
  const { includeBranding = true, labelSize = 'medium' } = options;
  
  const labelSizes = {
    small: { width: '39.7mm', height: '20mm', qrSize: '15mm', fontSize: '6px' },
    medium: { width: '60mm', height: '40mm', qrSize: '25mm', fontSize: '8px' },
    large: { width: '70mm', height: '50mm', qrSize: '35mm', fontSize: '10px' }
  };
  
  const size = labelSizes[labelSize];
  
  return `
    <div style="
      width: ${size.width};
      height: ${size.height};
      border: 1px solid #ccc;
      padding: 2mm;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-family: Arial, sans-serif;
      font-size: ${size.fontSize};
      page-break-inside: avoid;
    ">
      <img src="${qrCodeDataUrl}" style="width: ${size.qrSize}; height: ${size.qrSize}; margin-bottom: 1mm;" alt="QR Code" />
      <div style="font-weight: bold; margin-bottom: 0.5mm; line-height: 1.1;">
        ${product.name.length > 25 ? product.name.substring(0, 25) + '...' : product.name}
      </div>
      <div style="color: #666; margin-bottom: 0.5mm;">
        ${product.item_code}
      </div>
      ${product.brand ? `<div style="color: #666; font-size: calc(${size.fontSize} * 0.8);">${product.brand}</div>` : ''}
      ${includeBranding ? `<div style="color: #666; font-size: calc(${size.fontSize} * 0.7); margin-top: 0.5mm;">www.musterix.com</div>` : ''}
    </div>
  `;
};

/**
 * QR resolution result interface
 */
export interface QRResolutionResult {
  success: boolean;
  type: 'success' | 'not_found' | 'not_in_company' | 'invalid';
  message: string;
  productId?: string;
  isCompanySpecific?: boolean;
}

/**
 * Resolve QR code data for a specific company with validation
 * SECURITY: Always validates company access and ownership
 */
export const resolveQrForCompany = async (qrData: string, companyId: string): Promise<QRResolutionResult> => {
  if (!companyId) {
    return {
      success: false,
      type: 'invalid',
      message: 'Şirket bilgisi gerekli'
    };
  }

  // Parse the QR code
  const parsed = parseQRCode(qrData);
  if (!parsed) {
    return {
      success: false,
      type: 'invalid',
      message: 'Geçersiz QR kod formatı'
    };
  }

  // Check if it's a secure QR code with company validation
  if (parsed.isSecure && parsed.companyId) {
    if (parsed.companyId !== companyId) {
      return {
        success: false,
        type: 'not_in_company',
        message: 'Bu QR kod farklı bir şirkete ait. Sadece kendi şirketinizin ürünlerini görüntüleyebilirsiniz.'
      };
    }
    
    return {
      success: true,
      type: 'success',
      message: 'Ürün başarıyla bulundu',
      productId: parsed.productId,
      isCompanySpecific: true
    };
  }

  // For legacy/insecure QR codes, we assume they might be valid but warn
  console.warn('QR code uses legacy format without company validation');
  return {
    success: true,
    type: 'success',
    message: 'Ürün bulundu (eski format)',
    productId: parsed.productId,
    isCompanySpecific: false
  };
};

/**
 * Log QR scan activity for analytics and security
 * This helps track QR code usage and detect potential security issues
 */
export const logQrScanActivity = async (
  qrData: string, 
  userId: string, 
  companyId: string, 
  result: QRResolutionResult
): Promise<void> => {
  try {
    // In a real implementation, this would save to a database
    // For now, we'll log to console for debugging
    const logEntry = {
      timestamp: new Date().toISOString(),
      qrData: qrData.substring(0, 100), // Truncate for privacy
      userId,
      companyId,
      success: result.success,
      type: result.type,
      productId: result.productId,
      isCompanySpecific: result.isCompanySpecific,
      userAgent: navigator.userAgent
    };

    console.log('QR Scan Activity:', logEntry);

    // TODO: In production, send this to your analytics service
    // Example:
    // await fetch('/api/analytics/qr-scan', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(logEntry)
    // });

  } catch (error) {
    console.error('Failed to log QR scan activity:', error);
    // Don't throw error as this is non-critical logging
  }
};
